import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { useProfileDraft } from "@/context/ProfileDraftContext";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import {
  Layers,
  Plus,
  Sparkles,
  ArrowRight,
  Loader2,
  ShieldAlert,
  History,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { SortablePageItem } from "./SortablePageItem";
import { CreatePageModal } from "./CreatePageModal";
import { PageSettingsModal } from "./PageSettingsModal";
import { DeletePageDialog } from "./DeletePageDialog";
import { PageSectionsModal } from "./PageSectionsModal";
import type { PageItem } from "./types";

export function PagesManagement() {
  const { userTier, onUpgradeClick } = useProfileDraft();
  const utils = trpc.useUtils();

  const { data: profile } = trpc.profile.getMyProfile.useQuery(undefined, {
    staleTime: 10_000,
  });

  const {
    data: rawPages = [],
    isLoading,
    isError,
    error,
    refetch,
  } = trpc.pages.listPages.useQuery(undefined, {
    staleTime: 5_000,
  });

  const pages: PageItem[] = (rawPages || []) as PageItem[];

  // Local state for modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingPage, setEditingPage] = useState<PageItem | null>(null);
  const [deletingPage, setDeletingPage] = useState<PageItem | null>(null);
  const [sectionsModalPage, setSectionsModalPage] = useState<PageItem | null>(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Tier Limits
  const isStarter = userTier === "starter";
  const isPro = userTier === "pro";

  const maxPages = isStarter ? 1 : isPro ? 5 : Infinity;
  const pageCount = pages.length;
  const isPageLimitReached = pageCount >= maxPages;

  // Reorder mutation
  const reorderMutation = trpc.pages.reorderPages.useMutation({
    onSuccess: () => {
      toast.success("Page navigation order updated!");
      utils.pages.listPages.invalidate();
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message || "Failed to update page order.");
      refetch();
    },
  });

  // Toggle visibility mutation
  const toggleVisibilityMutation = trpc.pages.updatePage.useMutation({
    onSuccess: (updated: { title: string; visible: boolean }) => {
      toast.success(
        `"${updated.title}" is now ${updated.visible ? "visible in navigation" : "hidden from navigation"}.`
      );
      utils.pages.listPages.invalidate();
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message || "Failed to toggle page visibility.");
    },
  });

  // Handle Drag & Drop
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = pages.findIndex((p: PageItem) => p.id === active.id);
    const newIndex = pages.findIndex((p: PageItem) => p.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Home page must remain at position 0
    if (oldIndex === 0 || newIndex === 0) {
      toast.error("The Home page is permanently fixed at the top position.");
      return;
    }

    const reordered = arrayMove(pages, oldIndex, newIndex);
    const pageIds = reordered.map((p: PageItem) => p.id);

    reorderMutation.mutate({ pageIds });
  };

  // Keyboard / accessible reordering
  const handleMovePage = (page: PageItem, direction: "up" | "down") => {
    const currentIndex = pages.findIndex((p: PageItem) => p.id === page.id);
    if (currentIndex === -1 || page.isHome) return;

    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    // Cannot move into position 0 (Home page) or past the end
    if (targetIndex < 1 || targetIndex >= pages.length) return;

    const reordered = arrayMove(pages, currentIndex, targetIndex);
    const pageIds = reordered.map((p: PageItem) => p.id);

    reorderMutation.mutate({ pageIds });
  };

  const handleToggleVisibility = (page: PageItem) => {
    if (page.isHome) return;
    toggleVisibilityMutation.mutate({
      pageId: page.id,
      visible: !page.visible,
    });
  };

  const handleCreateClick = () => {
    if (isPageLimitReached) {
      onUpgradeClick("multi_page");
    } else {
      setCreateModalOpen(true);
    }
  };

  const existingSlugs = useMemo(() => pages.map((p: PageItem) => p.slug), [pages]);

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 space-y-8 animate-in fade-in duration-200">
      {/* ── Top Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6 border-b border-border">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center shadow-xs">
              <Layers className="h-5 w-5" />
            </div>
            <h1 className="text-xl font-bold tracking-tight text-foreground">
              Pages & Navigation
            </h1>
          </div>
          <p className="text-xs text-muted-foreground max-w-xl">
            Organize multi-page routes, customize navigation hierarchy, and manage URL slugs for your academic portfolio.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <Button
            type="button"
            size="sm"
            onClick={handleCreateClick}
            className="gap-1.5 shadow-xs"
          >
            <Plus className="h-4 w-4" />
            Add Page
          </Button>
        </div>
      </div>

      {/* ── Tier Usage & Entitlement Banner ── */}
      {isStarter ? (
        <div className="p-4 sm:p-5 rounded-2xl border border-amber-200/80 dark:border-amber-900/60 bg-gradient-to-r from-amber-50 to-orange-50/40 dark:from-amber-950/20 dark:to-orange-950/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 text-[10px] font-bold">
              <Sparkles className="h-3 w-3" /> STARTER PLAN (1 PAGE MAX)
            </div>
            <h2 className="text-sm font-bold text-foreground">
              Unlock Multi-Page Academic Routing
            </h2>
            <p className="text-xs text-muted-foreground max-w-lg leading-relaxed">
              Create dedicated sub-routes (/research, /teaching, /lab-team) with custom navigation while sharing a unified theme and header. Upgrade to Pro for up to 5 pages.
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={() => onUpgradeClick("multi_page")}
            className="bg-amber-600 hover:bg-amber-700 text-white shrink-0 gap-1.5 text-xs font-semibold shadow-xs"
          >
            Upgrade to Pro
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : isPro ? (
        <div className="flex items-center justify-between p-3.5 rounded-xl border border-border bg-slate-50 dark:bg-muted/20 text-xs">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="font-semibold text-primary border-primary/30">
              PRO PLAN
            </Badge>
            <span className="text-muted-foreground">
              Using <strong className="text-foreground">{pageCount} of {maxPages}</strong> allowed pages.
            </span>
          </div>
          {isPageLimitReached ? (
            <button
              type="button"
              onClick={() => onUpgradeClick("multi_page")}
              className="text-primary hover:underline font-semibold flex items-center gap-1 cursor-pointer text-xs"
            >
              Upgrade to Premium for unlimited pages
              <ArrowRight className="h-3 w-3" />
            </button>
          ) : (
            <span className="text-muted-foreground">
              {maxPages - pageCount} page{maxPages - pageCount === 1 ? "" : "s"} remaining
            </span>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-between p-3.5 rounded-xl border border-border bg-slate-50 dark:bg-muted/20 text-xs">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="font-semibold text-emerald-600 dark:text-emerald-400 border-emerald-500/30">
              PREMIUM PLAN
            </Badge>
            <span className="text-muted-foreground">
              Unlimited pages active ({pageCount} page{pageCount === 1 ? "" : "s"} created).
            </span>
          </div>
        </div>
      )}

      {/* ── Page List ── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between px-1">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Site Navigation Hierarchy
          </h2>
          <span className="text-[11px] text-muted-foreground">
            Drag items to adjust top navigation menu order
          </span>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-12 rounded-xl border border-border bg-card">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <div className="flex items-center gap-3 p-4 rounded-xl border border-destructive/20 bg-destructive/10 text-xs text-destructive">
            <ShieldAlert className="h-5 w-5 shrink-0" />
            <div>
              <p className="font-semibold">Failed to load pages</p>
              <p className="text-[11px] opacity-90">{error?.message}</p>
            </div>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={pages.map((p: PageItem) => p.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2.5">
                {pages.map((page: PageItem, index: number) => (
                  <SortablePageItem
                    key={page.id}
                    page={page}
                    subdomain={profile?.subdomain}
                    isFirst={index <= 1} // Index 0 is Home, index 1 is first secondary page
                    isLast={index === pages.length - 1}
                    onEdit={setEditingPage}
                    onManageSections={setSectionsModalPage}
                    onDelete={setDeletingPage}
                    onToggleVisibility={handleToggleVisibility}
                    onMoveUp={() => handleMovePage(page, "up")}
                    onMoveDown={() => handleMovePage(page, "down")}
                    disabled={reorderMutation.isPending}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* ── Redirect & Link Integrity Note ── */}
      <div className="p-4 rounded-xl border border-border bg-muted/20 space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
          <History className="h-4 w-4 text-primary" />
          <span>Automatic 301 Redirect Protection</span>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          If you ever modify a page&apos;s URL slug, Profolio permanently preserves the previous slug as an automatic 301 redirect. Previous citations, QR codes, and academic bookmarks will continue to resolve to your canonical route.
        </p>
      </div>

      {/* ── Modals ── */}
      <CreatePageModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        existingSlugs={existingSlugs}
        subdomain={profile?.subdomain}
        onSuccess={() => refetch()}
      />

      <PageSettingsModal
        page={editingPage}
        open={Boolean(editingPage)}
        onOpenChange={(open) => !open && setEditingPage(null)}
        existingSlugs={existingSlugs}
        subdomain={profile?.subdomain}
        onSuccess={() => refetch()}
      />

      <DeletePageDialog
        page={deletingPage}
        open={Boolean(deletingPage)}
        onOpenChange={(open) => !open && setDeletingPage(null)}
        onOpenManageSections={(p) => setSectionsModalPage(p)}
        onSuccess={() => refetch()}
      />

      <PageSectionsModal
        page={sectionsModalPage}
        allPages={pages}
        open={Boolean(sectionsModalPage)}
        onOpenChange={(open) => !open && setSectionsModalPage(null)}
      />
    </div>
  );
}
